<template>
  <div class="block-carousel">
    <div
      ref="viewportRef"
      class="block-carousel__viewport"
      :class="{ 'block-carousel__viewport--dragging': isDragging }"
      @pointerdown="onPointerDown"
      @click.capture="onViewportClick"
      @dragstart.prevent
    >
      <div
        class="block-carousel__track"
        :class="{ 'block-carousel__track--centered': centered }"
        :style="trackStyle"
      >
        <slot />
      </div>
    </div>
    <button
      v-if="navigation && isNavigationRequired"
      type="button"
      class="block-carousel__nav block-carousel__nav--prev block-carousel__nav--left"
      aria-label="Previous slides"
      @click="prev"
    >
      <span class="arrow" aria-hidden="true" />
    </button>
    <button
      v-if="navigation && isNavigationRequired"
      type="button"
      class="block-carousel__nav block-carousel__nav--next block-carousel__nav--right"
      aria-label="Next slides"
      @click="next"
    >
      <span class="arrow" aria-hidden="true" />
    </button>
    <div v-if="pagination && pageCount > 1" class="block-carousel__pagination">
      <button
        v-for="page in pageCount"
        :key="page"
        type="button"
        class="block-carousel__dot"
        :class="{ 'block-carousel__dot--active': currentPage === page - 1 }"
        :aria-label="`Go to page ${page}`"
        @click="goToPage(page - 1)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
  type CSSProperties,
} from 'vue';

import type { PerPageBreakpoint } from './BlockCarousel.types';

const props = defineProps({
  centerMode: { type: [Boolean, String], default: false },
  navigationEnabled: { type: [Boolean, String], default: false },
  paginationEnabled: { type: [Boolean, String], default: false },
  perPageCustom: { type: [String, Array], default: '' },
  autoplay: { type: [Boolean, String], default: false },
  loop: { type: [Boolean, String], default: false },
  autoplayTimeout: { type: [Number, String], default: 4000 },
  scrollPerPage: { type: [Boolean, String], default: false },
});

const RESISTANCE_COEF = 20;
const MIN_SWIPE_DISTANCE = 8;
const TRANSITION_MS = 500;

const isTrue = (value: boolean | string): boolean => value === true || value === 'true';

const centerMode = computed(() => isTrue(props.centerMode));
const navigation = computed(() => isTrue(props.navigationEnabled));
const pagination = computed(() => isTrue(props.paginationEnabled));
const autoplayEnabled = computed(() => isTrue(props.autoplay));
const loopEnabled = computed(() => isTrue(props.loop));
const scrollPerPage = computed(() => isTrue(props.scrollPerPage));
const interval = computed(() => Number(props.autoplayTimeout) || 4000);

const viewportRef = ref<HTMLElement | null>(null);
const windowWidth = ref(0);
const carouselWidth = ref(0);
const slideCount = ref(0);
const offsetPx = ref(0);
const dragOffsetPx = ref(0);
const dragMomentum = ref(0);
const isDragging = ref(false);
const dragStartX = ref(0);
const dragStartTime = ref(0);
const suppressClick = ref(false);
const activePointerId = ref<number | null>(null);

const breakpoints = computed<PerPageBreakpoint[]>(() => {
  if (Array.isArray(props.perPageCustom)) {
    return props.perPageCustom.filter(
      (pair): pair is PerPageBreakpoint =>
        Array.isArray(pair) && typeof pair[0] === 'number' && typeof pair[1] === 'number'
    );
  }

  if (typeof props.perPageCustom !== 'string' || '' === props.perPageCustom) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(props.perPageCustom);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (pair): pair is PerPageBreakpoint =>
        Array.isArray(pair) && typeof pair[0] === 'number' && typeof pair[1] === 'number'
    );
  } catch {
    return [];
  }
});

const perPage = computed(() => {
  const sorted = [...breakpoints.value].sort((a, b) => b[0] - a[0]);
  const match = sorted.find(([width]) => windowWidth.value >= width);

  return Math.max(1, match?.[1] ?? 2);
});

const slideWidthPx = computed(() =>
  carouselWidth.value > 0 ? carouselWidth.value / perPage.value : 0
);
const maxOffsetPx = computed(() =>
  Math.max(slideWidthPx.value * (slideCount.value - perPage.value), 0)
);
const isNavigationRequired = computed(() => slideCount.value > perPage.value);
const centered = computed(() => centerMode.value && !isNavigationRequired.value);
const pageCount = computed(() =>
  scrollPerPage.value
    ? Math.ceil(slideCount.value / perPage.value)
    : Math.max(1, slideCount.value - perPage.value + 1)
);
const currentPage = computed(() =>
  scrollPerPage.value
    ? Math.round(offsetPx.value / slideWidthPx.value / perPage.value)
    : Math.round(offsetPx.value / slideWidthPx.value)
);

provide('blockCarouselSlideWidthPx', slideWidthPx);
provide('blockCarouselRegister', (): void => {
  slideCount.value += 1;
});

const resistedDragOffset = computed((): number => {
  if (!isDragging.value) {
    return 0;
  }

  const raw = dragOffsetPx.value;
  const next = offsetPx.value + raw;

  if (next < 0) {
    return -Math.sqrt(-RESISTANCE_COEF * raw);
  }

  if (next > maxOffsetPx.value) {
    return Math.sqrt(RESISTANCE_COEF * raw);
  }

  return raw;
});

const currentOffsetPx = computed((): number => {
  if (centered.value) {
    return 0;
  }

  return -(offsetPx.value + resistedDragOffset.value);
});

const trackStyle = computed(
  (): CSSProperties => ({
    transform: `translate3d(${currentOffsetPx.value}px, 0, 0)`,
    transition: isDragging.value ? 'none' : `transform ${TRANSITION_MS}ms ease`,
    visibility: slideWidthPx.value > 0 ? 'visible' : 'hidden',
  })
);

const clampOffset = (): void => {
  offsetPx.value = Math.max(0, Math.min(offsetPx.value, maxOffsetPx.value));
};

const snapOffset = (): void => {
  if (slideWidthPx.value <= 0) {
    return;
  }

  let offset = offsetPx.value;

  offset +=
    Math.max(-perPage.value + 1, Math.min(Math.round(dragMomentum.value), perPage.value - 1)) *
    slideWidthPx.value;

  const stepPx = scrollPerPage.value ? slideWidthPx.value * perPage.value : slideWidthPx.value;

  if (scrollPerPage.value) {
    const lastFullPageOffset = stepPx * Math.floor(slideCount.value / (perPage.value - 1));
    const remainderOffset =
      lastFullPageOffset + slideWidthPx.value * (slideCount.value % perPage.value);

    offset =
      offset > (lastFullPageOffset + remainderOffset) / 2
        ? remainderOffset
        : stepPx * Math.round(offset / stepPx);
  } else {
    offset = stepPx * Math.round(offset / stepPx);
  }

  offsetPx.value = Math.max(0, Math.min(offset, maxOffsetPx.value));
};

const goToOffset = (offset: number): void => {
  offsetPx.value = Math.max(0, Math.min(offset, maxOffsetPx.value));
};

const stepPx = computed(() =>
  scrollPerPage.value ? slideWidthPx.value * perPage.value : slideWidthPx.value
);

const next = (): void => {
  if (offsetPx.value >= maxOffsetPx.value) {
    goToOffset(loopEnabled.value ? 0 : maxOffsetPx.value);

    return;
  }
  goToOffset(offsetPx.value + stepPx.value);
};

const prev = (): void => {
  if (offsetPx.value <= 0) {
    goToOffset(loopEnabled.value ? maxOffsetPx.value : 0);

    return;
  }
  goToOffset(offsetPx.value - stepPx.value);
};

const goToPage = (page: number): void => {
  goToOffset(page * stepPx.value);
};

const onDocumentPointerMove = (event: PointerEvent): void => {
  if (!isDragging.value || activePointerId.value !== event.pointerId) {
    return;
  }

  dragOffsetPx.value = dragStartX.value - event.clientX;
};

const finishDrag = (event: PointerEvent): void => {
  if (!isDragging.value || activePointerId.value !== event.pointerId) {
    return;
  }

  document.removeEventListener('pointermove', onDocumentPointerMove);
  document.removeEventListener('pointerup', finishDrag);
  document.removeEventListener('pointercancel', finishDrag);

  const deltaX = dragStartX.value - event.clientX;
  dragMomentum.value =
    event.timeStamp > dragStartTime.value ? deltaX / (event.timeStamp - dragStartTime.value) : 0;

  if (Math.abs(deltaX) >= MIN_SWIPE_DISTANCE) {
    suppressClick.value = true;
    dragOffsetPx.value += Math.sign(deltaX) * (stepPx.value / 2);
  }

  offsetPx.value += resistedDragOffset.value;
  dragOffsetPx.value = 0;
  isDragging.value = false;
  activePointerId.value = null;

  snapOffset();
  startAutoplay();
};

const onPointerDown = (event: PointerEvent): void => {
  if (0 !== event.button || !isNavigationRequired.value) {
    return;
  }

  stopAutoplay();
  isDragging.value = true;
  dragStartX.value = event.clientX;
  dragStartTime.value = event.timeStamp;
  dragOffsetPx.value = 0;
  dragMomentum.value = 0;
  activePointerId.value = event.pointerId;

  document.addEventListener('pointermove', onDocumentPointerMove);
  document.addEventListener('pointerup', finishDrag);
  document.addEventListener('pointercancel', finishDrag);
};

const onViewportClick = (event: MouseEvent): void => {
  if (!suppressClick.value) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  suppressClick.value = false;
};

let autoplayTimer: ReturnType<typeof setInterval> | null = null;
let resizeObserver: ResizeObserver | null = null;

const stopAutoplay = (): void => {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
};

const startAutoplay = (): void => {
  stopAutoplay();
  if (!autoplayEnabled.value || !isNavigationRequired.value) {
    return;
  }
  autoplayTimer = setInterval(() => next(), interval.value);
};

const measureCarouselWidth = (): void => {
  carouselWidth.value = viewportRef.value?.clientWidth ?? 0;
  clampOffset();
};

const onResize = (): void => {
  windowWidth.value = window.innerWidth;
  measureCarouselWidth();
};

onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize);

  if (viewportRef.value) {
    resizeObserver = new ResizeObserver(() => {
      measureCarouselWidth();
    });
    resizeObserver.observe(viewportRef.value);
  }

  void nextTick(startAutoplay);
});

watch(slideCount, () => {
  clampOffset();
  void nextTick(startAutoplay);
});

watch([perPage, slideWidthPx], () => {
  clampOffset();
  startAutoplay();
});

onBeforeUnmount(() => {
  stopAutoplay();
  window.removeEventListener('resize', onResize);
  resizeObserver?.disconnect();
  document.removeEventListener('pointermove', onDocumentPointerMove);
  document.removeEventListener('pointerup', finishDrag);
  document.removeEventListener('pointercancel', finishDrag);
});
</script>

<style lang="scss" scoped>
.block-carousel {
  position: relative;
}

.block-carousel__viewport {
  overflow: hidden;
  touch-action: pan-y;
  cursor: grab;
  user-select: none;

  &--dragging {
    cursor: grabbing;
  }
}

.block-carousel__track {
  display: flex;
  flex-direction: row;
  backface-visibility: hidden;

  &--centered {
    justify-content: center;
  }
}

.block-carousel__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  cursor: pointer;
  padding: 5px;

  &--prev {
    left: 0;
  }

  &--next {
    right: 0;
  }
}

.block-carousel__pagination {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}

.block-carousel__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 0;
  background-color: #c0c0c0;
  cursor: pointer;
  padding: 0;

  &--active {
    background-color: #000;
  }
}
</style>
